import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Portal,
  Radio,
  RadioGroup,
  Stack,
  Text,
  Textarea,
  useToast,
} from '@chakra-ui/react';
import { Check, X, CircleHelp } from 'lucide-react';

/**
 * ComplianceDecisionPopover
 *
 * Click-to-edit surface for a single (contact, draft) compliance cell.
 * Anchored on the Status chip in `ReviewerCampaignTable`; renders a
 * verdict options (plus implicit not-reviewed outside this form),
 * persists via the parent-supplied `onSave` (wired to
 * `useReviewerDecisionMutation` upstream), and self-closes on success.
 *
 * Editing eligibility is decided by the caller (the chip is wrapped in
 * a Popover here; the chip itself is *always* the trigger but the
 * popover only opens when this component is rendered). Non-editable
 * cells render the bare chip without this wrapper — see
 * `ReviewerCampaignTable.RightCell`.
 *
 * Keyboard:
 *   - Enter inside the textarea inserts a newline (default).
 *   - Cmd/Ctrl+Enter saves.
 *   - Esc closes (Chakra default).
 */

const NOTE_MIN_NON_COMPLIANT = 3;
const NOTE_REQUIRED_STATUSES = new Set(['non_compliant', 'needs_human_review']);

const ComplianceDecisionPopover = ({
  trigger,
  initialStatus,
  initialNote,
  isSaving,
  onSave,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [status, setStatus] = useState(initialStatus || 'compliant');
  const [note, setNote] = useState(initialNote || '');
  const [submitError, setSubmitError] = useState(null);
  const textareaRef = useRef(null);
  const toast = useToast();

  // Re-seed local state every time the popover opens so a cancelled
  // edit doesn't bleed into the next open.
  useEffect(() => {
    if (!isOpen) return;
    const seed = initialStatus === 'needs_human_review'
      ? 'needs_human_review'
      : initialStatus === 'non_compliant'
        ? 'non_compliant'
        : 'compliant';
    setStatus(seed);
    setNote(initialNote || '');
    setSubmitError(null);
  }, [isOpen, initialStatus, initialNote]);

  const noteRequired =
    NOTE_REQUIRED_STATUSES.has(status);

  const canSave = (() => {
    if (isSaving) return false;
    if (noteRequired) {
      return note.trim().length >= NOTE_MIN_NON_COMPLIANT;
    }
    return true;
  })();

  const handleSave = async () => {
    if (!canSave) return;
    setSubmitError(null);
    try {
      await onSave({
        status,
        note: note.trim() || null,
      });
      setIsOpen(false);
    } catch (err) {
      const code = err?.code;
      if (code === 'content_changed') {
        // The advisor mutated the cell mid-review. The optimistic
        // update was already rolled back upstream; surface a toast
        // explaining what happened so the reviewer doesn't think their
        // click was lost.
        toast({
          title: 'Draft changed during review',
          description:
            'The advisor edited this draft after you opened the page. The latest content is now loaded — please re-check before approving.',
          status: 'warning',
          duration: 6000,
          isClosable: true,
        });
        setIsOpen(false);
        return;
      }
      setSubmitError(
        err?.message || 'Could not save your decision. Please try again.',
      );
    }
  };

  const handleKeyDown = (e) => {
    if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
      e.preventDefault();
      handleSave();
    }
  };

  const showNoteWarning =
    noteRequired && note.trim().length < NOTE_MIN_NON_COMPLIANT;

  return (
    <Popover
      isOpen={isOpen}
      onOpen={() => setIsOpen(true)}
      onClose={() => setIsOpen(false)}
      placement="bottom-start"
      closeOnBlur
      initialFocusRef={textareaRef}
      gutter={6}
    >
      <PopoverTrigger>
        <Box
          as="button"
          type="button"
          aria-label="Edit compliance verdict"
          display="inline-flex"
          alignItems="center"
          background="transparent"
          border="none"
          p={0}
          cursor="pointer"
          transition="transform 120ms ease, filter 120ms ease"
          _hover={{ filter: 'brightness(1.10)', transform: 'translateY(-1px)' }}
          _active={{ transform: 'translateY(0)' }}
          _focusVisible={{
            outline: '2px solid rgba(255,255,255,0.22)',
            outlineOffset: '2px',
            borderRadius: 'full',
          }}
        >
          {trigger}
        </Box>
      </PopoverTrigger>
      <Portal>
        <PopoverContent
          bg="#0f0f0f"
          color="#fafafa"
          border="1px solid rgba(255,255,255,0.12)"
          borderRadius="10px"
          boxShadow="0 12px 40px rgba(0,0,0,0.55)"
          width="420px"
          maxW="min(420px, calc(100vw - 48px))"
          _focus={{ boxShadow: '0 12px 40px rgba(0,0,0,0.55)' }}
        >
          <PopoverArrow bg="#0f0f0f" boxShadow="-1px -1px 0 rgba(255,255,255,0.12)" />
          <PopoverBody p={4}>
            <Stack spacing={3}>
              <Text
                fontSize="11px"
                fontWeight="600"
                letterSpacing="0.05em"
                textTransform="uppercase"
                color="rgba(255,255,255,0.55)"
              >
                Compliance verdict
              </Text>

              <RadioGroup value={status} onChange={(next) => setStatus(next)}>
                <Stack spacing={2}>
                  <Radio
                    value="compliant"
                    colorScheme="green"
                    borderColor="rgba(255,255,255,0.25)"
                    size="sm"
                  >
                    <Box display="inline-flex" alignItems="center" gap="6px">
                      <Check size={13} strokeWidth={2.5} color="#34D399" />
                      <Text fontSize="sm" color="rgba(255,255,255,0.92)">
                        Compliant
                      </Text>
                    </Box>
                  </Radio>
                  <Radio
                    value="needs_human_review"
                    colorScheme="purple"
                    borderColor="rgba(255,255,255,0.25)"
                    size="sm"
                  >
                    <Box display="inline-flex" alignItems="center" gap="6px">
                      <CircleHelp size={13} strokeWidth={2.5} color="#A78BFA" />
                      <Text fontSize="sm" color="rgba(255,255,255,0.92)">
                        Needs human review
                      </Text>
                    </Box>
                  </Radio>
                  <Radio
                    value="non_compliant"
                    colorScheme="red"
                    borderColor="rgba(255,255,255,0.25)"
                    size="sm"
                  >
                    <Box display="inline-flex" alignItems="center" gap="6px">
                      <X size={13} strokeWidth={2.5} color="#EF4444" />
                      <Text fontSize="sm" color="rgba(255,255,255,0.92)">
                        Non-compliant
                      </Text>
                    </Box>
                  </Radio>
                </Stack>
              </RadioGroup>

              <Box>
                <Text
                  fontSize="11px"
                  fontWeight="600"
                  letterSpacing="0.05em"
                  textTransform="uppercase"
                  color="rgba(255,255,255,0.55)"
                  mb={1.5}
                >
                  Note{' '}
                  {noteRequired ? (
                    <Text as="span" color="rgba(248,113,113,0.88)">
                      (required — min {NOTE_MIN_NON_COMPLIANT} chars)
                    </Text>
                  ) : (
                    <Text as="span" color="rgba(255,255,255,0.35)">
                      (optional)
                    </Text>
                  )}
                </Text>
                <Textarea
                  ref={textareaRef}
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder={
                    status === 'non_compliant'
                      ? 'Why is this draft not compliant?'
                      : status === 'needs_human_review'
                        ? 'What facts or approvals does a human need to verify?'
                        : 'Optional context for the advisor.'
                  }
                  bg="rgba(255,255,255,0.04)"
                  border="1px solid rgba(255,255,255,0.10)"
                  color="#fafafa"
                  fontSize="sm"
                  _placeholder={{ color: 'rgba(255,255,255,0.35)' }}
                  _focus={{
                    borderColor: 'rgba(255,255,255,0.30)',
                    boxShadow: 'none',
                  }}
                  _hover={{ borderColor: 'rgba(255,255,255,0.18)' }}
                  rows={8}
                  minH="148px"
                  resize="vertical"
                />
                {showNoteWarning && (
                  <Text fontSize="11px" color="rgba(239,68,68,0.85)" mt={1}>
                    {`Add at least ${NOTE_MIN_NON_COMPLIANT} characters`}
                  </Text>
                )}
              </Box>

              {submitError ? (
                <Text fontSize="xs" color="rgba(239,68,68,0.95)">
                  {submitError}
                </Text>
              ) : null}

              <Box display="flex" justifyContent="flex-end" gap="8px">
                <Button
                  variant="ghost"
                  size="sm"
                  color="rgba(255,255,255,0.65)"
                  _hover={{ bg: 'rgba(255,255,255,0.06)' }}
                  onClick={() => setIsOpen(false)}
                  isDisabled={isSaving}
                >
                  Cancel
                </Button>
                <Button
                  size="sm"
                  bg="#fafafa"
                  color="#0a0a0a"
                  _hover={{ opacity: 0.88 }}
                  _active={{ opacity: 0.78 }}
                  onClick={handleSave}
                  isLoading={isSaving}
                  isDisabled={!canSave}
                  fontWeight="600"
                  borderRadius="full"
                  px={4}
                >
                  Save
                </Button>
              </Box>
            </Stack>
          </PopoverBody>
        </PopoverContent>
      </Portal>
    </Popover>
  );
};

export default ComplianceDecisionPopover;
